/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Button, Tag } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NAVY   = '#1a3a5c';
const toN    = v => Number(v || 0);

function KPI({ label, value, color=NAVY, sub }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}
function ChartCard({ title, children, height=280 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

function scoreColor(s) { return s >= 80 ? '#52c41a' : s >= 60 ? '#faad14' : '#f5222d'; }
function scoreBand(s)  { return s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Fair' : 'Poor'; }

export default function FleetAnalyticsDriverScorecard() {
    const [drivers,    setDrivers]    = useState([]);
    const [incidents,  setIncidents]  = useState([]);
    const [trips,      setTrips]      = useState([]);
    const [loading,    setLoading]    = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2, r3] = await Promise.all([
                FleetService.reportDrivers(),
                FleetService.getIncidents(),
                FleetService.getAllTripLogs(),
            ]);
            if (Number(r1?.data?.code) === 200) setDrivers(r1.data.result    || []);
            if (Number(r2?.data?.code) === 200) setIncidents(r2.data.result  || []);
            if (Number(r3?.data?.code) === 200) setTrips(r3.data.result?.data || r3.data.result || []);
        } catch (e) { message.error('Failed to load data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    // Build scorecard per driver
    const scorecards = useMemo(() => {
        return drivers.map(d => {
            // Incidents for this driver's vehicle
            const vIncidents = incidents.filter(i =>
                String(i.vehicle_id) === String(d.vehicle_id) ||
                (i.driver_name && d.driver_name && i.driver_name.toLowerCase() === d.driver_name.toLowerCase())
            );
            const openIncidents   = vIncidents.filter(i => i.status === 'Open' || !i.date_resolved).length;
            const closedIncidents = vIncidents.filter(i => i.status !== 'Open' && i.date_resolved).length;

            // Trips for this vehicle
            const vTrips = trips.filter(t => String(t.vehicle_id) === String(d.vehicle_id));
            const totalKm = vTrips.reduce((s,t) => s + (toN(t.odometer_end) - toN(t.odometer_start)), 0);
            const totalFuel = vTrips.reduce((s,t) => s + toN(t.fuel_litres), 0);
            const consumption = totalKm > 0 ? +(totalFuel / totalKm * 100).toFixed(1) : 0;

            // Licence check
            const licExpiry = d.licence_expiry && d.licence_expiry !== '0000-00-00'
                ? Math.ceil((new Date(d.licence_expiry) - new Date()) / 86400000) : 999;
            const licScore = licExpiry < 0 ? 0 : licExpiry < 30 ? 40 : licExpiry < 90 ? 70 : 100;

            // Composite score (0–100)
            const incidentScore  = Math.max(0, 100 - openIncidents * 20 - closedIncidents * 5);
            const consumptScore  = consumption === 0 ? 60 : consumption < 12 ? 100 : consumption < 20 ? 75 : consumption < 30 ? 50 : 20;
            const activityScore  = vTrips.length > 10 ? 100 : vTrips.length > 5 ? 75 : vTrips.length > 0 ? 50 : 20;
            const composite      = Math.round((incidentScore * 0.4 + consumptScore * 0.3 + activityScore * 0.2 + licScore * 0.1));

            return {
                driverName:    d.driver_name,
                reg:           d.registration_no,
                type:          d.vehicle_type,
                facility:      d.facility,
                licExpiry:     d.licence_expiry,
                licDays:       licExpiry,
                openIncidents, closedIncidents,
                tripCount:     vTrips.length,
                totalKm:       Math.round(totalKm),
                consumption,
                incidentScore, consumptScore, activityScore, licScore,
                score:         composite,
                band:          scoreBand(composite),
            };
        }).sort((a,b) => b.score - a.score);
    }, [drivers, incidents, trips]);

    const totalDrivers  = scorecards.length;
    const excellentCount= scorecards.filter(d=>d.band==='Excellent').length;
    const poorCount     = scorecards.filter(d=>d.band==='Poor').length;
    const expiredLic    = scorecards.filter(d=>d.licDays<0).length;
    const avgScore      = totalDrivers ? Math.round(scorecards.reduce((s,d)=>s+d.score,0)/totalDrivers) : 0;

    // Score distribution
    const bandPie = [
        { name:'Excellent', value:excellentCount,                           fill:'#52c41a' },
        { name:'Good',      value:scorecards.filter(d=>d.band==='Good').length,   fill:'#faad14' },
        { name:'Fair',      value:scorecards.filter(d=>d.band==='Fair').length,   fill:'#fa8c16' },
        { name:'Poor',      value:poorCount,                                fill:'#f5222d' },
    ].filter(d=>d.value>0);

    // Top/bottom 10 for bar chart
    const top10    = scorecards.slice(0, 10);
    const bottom10 = [...scorecards].reverse().slice(0, 10);

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(scorecards.map(d => ({
            'Driver':            d.driverName,
            'Vehicle':           d.reg,
            'Type':              d.type,
            'Facility':          d.facility,
            'Composite Score':   d.score,
            'Band':              d.band,
            'Trips':             d.tripCount,
            'Total KM':          d.totalKm,
            'Fuel L/100km':      d.consumption,
            'Open Incidents':    d.openIncidents,
            'Closed Incidents':  d.closedIncidents,
            'Licence Expiry':    d.licExpiry,
            'Licence Days Left': d.licDays < 0 ? 'EXPIRED' : d.licDays,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Driver Scorecard');
        saveAs(new Blob([XLSX.write(wb, { bookType:'xlsx', type:'array' })]),
            `Fleet_DriverScorecard_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Driver Scorecard</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Composite score: safety (40%) + fuel efficiency (30%) + activity (20%) + licence (10%)
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<ReloadOutlined />} onClick={load}/>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!scorecards.length}>Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {/* KPIs */}
                <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                    <KPI label="Drivers Tracked"  value={totalDrivers}  color={NAVY}/>
                    <KPI label="Fleet Avg Score"  value={`${avgScore}/100`} color={scoreColor(avgScore)} sub={scoreBand(avgScore)}/>
                    <KPI label="Excellent Drivers" value={excellentCount} color="#52c41a" sub="score ≥80"/>
                    <KPI label="Poor Performers"   value={poorCount}      color="#f5222d" sub="score &lt;40 · needs attention"/>
                    <KPI label="Expired Licences"  value={expiredLic}     color={expiredLic>0?'#f5222d':'#52c41a'} sub="immediate risk"/>
                </div>

                {/* Row 1 */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:14 }}>
                    <ChartCard title="Score Distribution" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={bandPie} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={95}
                                    label={({ name, value, percent }) =>
                                        value > 0 ? `${name}: ${value} (${(percent*100).toFixed(0)}%)` : ''}>
                                    {bandPie.map((d,i) => <Cell key={i} fill={d.fill}/>)}
                                </Pie>
                                <Tooltip/>
                                <Legend wrapperStyle={{ fontSize:11 }}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Top 10 Drivers by Score" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={top10.map(d=>({ name:d.driverName||d.reg||'—', score:d.score }))}
                                layout="vertical" margin={{ top:0, right:50, left:100, bottom:0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false}/>
                                <XAxis type="number" domain={[0,100]} tick={{ fontSize:10 }}/>
                                <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={95}/>
                                <Tooltip formatter={v=>[`${v}/100`, 'Score']}/>
                                <Bar dataKey="score" radius={[0,4,4,0]}>
                                    {top10.map((d,i) => <Cell key={i} fill={scoreColor(d.score)}/>)}
                                    <LabelList dataKey="score" position="right" style={{ fontSize:9 }}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* Bottom 10 */}
                {poorCount > 0 && (
                    <ChartCard title="⚠ Drivers Needing Attention (lowest scores)" height={240}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={bottom10.map(d=>({ name:d.driverName||d.reg||'—',
                                    incScore:d.incidentScore, fuelScore:d.consumptScore,
                                    actScore:d.activityScore, licScore:d.licScore }))}
                                margin={{ top:5, right:10, left:100, bottom:5 }} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false}/>
                                <XAxis type="number" domain={[0,100]} tick={{ fontSize:10 }}/>
                                <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={95}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{ fontSize:10 }}/>
                                <Bar dataKey="incScore"  name="Safety"     stackId="a" fill="#f5222d"/>
                                <Bar dataKey="fuelScore" name="Fuel"       stackId="a" fill="#fa8c16"/>
                                <Bar dataKey="actScore"  name="Activity"   stackId="a" fill={NAVY}/>
                                <Bar dataKey="licScore"  name="Licence"    stackId="a" fill="#722ed1" radius={[0,4,4,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}

                {/* Detail table */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                        <thead>
                            <tr style={{ background:NAVY, color:'#fff' }}>
                                {['Driver','Vehicle','Score','Band','Trips','KM','L/100km','Incidents','Licence'].map(h =>
                                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:600 }}>{h}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {scorecards.map((d,i) => (
                                <tr key={i} style={{ background:i%2===0?'#fff':'#f9f9f9', borderBottom:'1px solid #f0f0f0' }}>
                                    <td style={{ padding:'8px 12px', fontWeight:600 }}>{d.driverName||'—'}</td>
                                    <td style={{ padding:'8px 12px' }}>{d.reg}</td>
                                    <td style={{ padding:'8px 12px' }}>
                                        <span style={{ fontWeight:700, color:scoreColor(d.score) }}>{d.score}</span>
                                    </td>
                                    <td style={{ padding:'8px 12px' }}>
                                        <Tag color={scoreColor(d.score)}>{d.band}</Tag>
                                    </td>
                                    <td style={{ padding:'8px 12px', textAlign:'right' }}>{d.tripCount}</td>
                                    <td style={{ padding:'8px 12px', textAlign:'right' }}>{d.totalKm.toLocaleString()}</td>
                                    <td style={{ padding:'8px 12px', textAlign:'right',
                                        color: d.consumption > 20 ? '#f5222d' : d.consumption > 12 ? '#fa8c16' : '#52c41a' }}>
                                        {d.consumption > 0 ? d.consumption : '—'}
                                    </td>
                                    <td style={{ padding:'8px 12px', textAlign:'center' }}>
                                        {d.openIncidents > 0 && <Tag color="red">{d.openIncidents} open</Tag>}
                                        {d.closedIncidents > 0 && <Tag color="default">{d.closedIncidents} closed</Tag>}
                                        {d.openIncidents === 0 && d.closedIncidents === 0 && '—'}
                                    </td>
                                    <td style={{ padding:'8px 12px' }}>
                                        {d.licDays < 0 ? <Tag color="red">EXPIRED</Tag> :
                                         d.licDays < 30 ? <Tag color="orange">{d.licDays}d left</Tag> :
                                         d.licExpiry && d.licExpiry !== '0000-00-00' ? <Tag color="green">Valid</Tag> : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!loading && scorecards.length === 0 && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8, marginTop:14 }}>
                        No driver assignment data. Assign drivers to vehicles in Driver Assignments.
                    </div>
                )}
            </Spin>
        </div>
    );
}